import type React from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { clsx } from "clsx";
import styled from "styled-components";

import { BaseButton } from "shared/atoms/BaseButton";
import { BLUE, ORANGE } from "theme/colors";

type Color = "blue" | "orange";

const iconColorMap: Record<Color, string> = {
  orange: ORANGE[100],
  blue: BLUE[100],
};

interface Props {
  icon: IconDefinition;
  label: string;
  buttonText: string;
  onClick: (event: React.MouseEvent) => void;
  color?: Color;
}

export const NotificationBox: React.FC<Props> = ({ icon, label, buttonText, onClick, color = "orange" }) => {
  return (
    <Container className={clsx(color)}>
      <Stack direction="row" spacing={1} alignItems="center">
        <FontAwesomeIcon icon={icon} style={{ color: iconColorMap[color] }} />
        <Typography variant="subtitle1">{label}</Typography>
      </Stack>
      <StyledBaseButton className={clsx(color)} onClick={onClick} text={buttonText} />
    </Container>
  );
};

const Container = styled.div`
  border-radius: 12px;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${props => props.theme.belowBreakpoint} {
    flex-direction: column;
    gap: 16px;
  }
  &.orange {
    background: ${ORANGE[10]};
    border: 1px solid ${ORANGE[100]};
  }
  &.blue {
    background: ${BLUE[10]};
    border: 1px solid ${BLUE[100]};
  }
`;

const StyledBaseButton = styled(BaseButton)`
  flex-grow: 0;
  &.orange {
    background: ${ORANGE[100]};
    &:hover {
      background: ${ORANGE[70]};
    }
  }
  &.blue {
    background: ${BLUE[100]};
    &:hover {
      background: ${BLUE[70]};
    }
  }
`;
